import React, { useEffect, useMemo } from 'react';

import getExpMessage from '@exp/components/localization/getExpMessage';
import { ExpLabel } from '@exp/components/localization';
import CarouselFlush from '@exp/components/carousel/CarouselFlush';

import { ModeConfig, ModeContext } from '../constants/modes';
import Brands50 from '../images/benefits/brandCount/50brands.png';
import Brands100 from '../images/benefits/brandCount/100brands.png';
import Brands200 from '../images/benefits/brandCount/200brands.png';
import Brands300 from '../images/benefits/brandCount/300brands.png';
import Brands400 from '../images/benefits/brandCount/400brands.png';
import Brands500 from '../images/benefits/brandCount/500brands.png';
import Brands600 from '../images/benefits/brandCount/600brands.png';
import Brands700 from '../images/benefits/brandCount/700brands.png';
import Brands800 from '../images/benefits/brandCount/800brands.png';
import Brands900 from '../images/benefits/brandCount/900brands.png';
import Brands1000 from '../images/benefits/brandCount/1000brands.png';
import RetailBoost from '../images/benefits/boostRetail.svg';
import ConnectExperts from '../images/benefits/connect.svg';
import Discounts from '../images/benefits/discounts.svg';
import DownloadApp from '../images/benefits/downloadApp.svg';
import ShopEarly from '../images/benefits/shopEarly.svg';
import StackSavings from '../images/benefits/stackSavings.svg';
import TestProducts from '../images/benefits/testProducts.svg';
import Gear from '../images/benefits/tryGear.svg';
import { fetchUserBrands, selectConfig, selectContext } from '../redux/context';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useSendEvent } from '../utilities/useAnalytics';

import './PerksBanner.less';

const ANALYTICS_PREFIX = 'APPLICATION_SUCCESS_';

const BENEFITS = [
  {
    id: 'retailBoost',
    image: RetailBoost,
    proSortOrder: null,
    retailSortOrder: 1, // 1 based
    textDefault: 'Get certified to confidently serve your customers.',
    titleDefault: 'Boost your retail expertise',
  },
  {
    id: 'brandCount',
    image: null, // dynamic
    proSortOrder: 1,
    retailSortOrder: 2,
    textDefault: 'Experience the best products firsthand.',
    titleDefault: 'Explore exclusive brands',
  },
  {
    id: 'discounts',
    image: Discounts,
    proSortOrder: 2,
    retailSortOrder: 3,
    textDefault: 'On average, experts',
    textAddendum: 'save over $300 per year.',
    titleDefault: 'Claim exclusive discounts',
  },
  {
    id: 'tryGear',
    image: Gear,
    proSortOrder: 3,
    retailSortOrder: 4,
    textDefault: 'Test new products and share feedback with top brands.',
    titleDefault: 'Be the first to try gear',
  },
  {
    id: 'stackSavings',
    image: StackSavings,
    proSortOrder: 4,
    retailSortOrder: 5,
    textDefault: 'Add extra savings at checkout!',
    titleDefault: 'Stack your savings with coupons',
  },
  {
    id: 'connectExperts',
    image: ConnectExperts,
    proSortOrder: 5,
    retailSortOrder: 6,
    textDefault: 'Share recommendations and discover new gear',
    titleDefault: 'Connect with other experts',
  },
  {
    id: 'shopEarly',
    image: ShopEarly,
    proSortOrder: 6,
    retailSortOrder: 7,
    textDefault: 'New deals drop frequently–act fast!',
    titleDefault: 'Shop early and often',
  },
  {
    id: 'download',
    image: DownloadApp,
    proSortOrder: 7,
    retailSortOrder: 8,
    textDefault: 'Your fav brands, just a tap away.',
    titleDefault: 'Download our mobile app',
  },
  {
    id: 'testProducts',
    image: TestProducts,
    proSortOrder: 8,
    retailSortOrder: 9,
    textDefault: 'Get first dibs on free samples.',
    titleDefault: 'Test products',
  },
];

const brandCounts = {
  50: Brands50,
  100: Brands100,
  200: Brands200,
  300: Brands300,
  400: Brands400,
  500: Brands500,
  600: Brands600,
  700: Brands700,
  800: Brands800,
  900: Brands900,
  1000: Brands1000,
};

const itemsPerPage = {
  xxxl: 5,
  xxl: 4,
  xl: 3,
  lg: 3,
  md: 2,
  sm: 2,
  xs: 1,
  xxs: 1,
};

const getBrandCountImage = (count: number) => {
  if (count > 99 && count < 1099) {
    return brandCounts[Math.floor(count / 100) * 100];
  }
  if (count > 1000) {
    return brandCounts['1000'];
  }
  if (count > 49) {
    return brandCounts['50'];
  }

  return null;
};

const PerksBanner = React.forwardRef(() => {
  const dispatch = useAppDispatch();
  const { mode }: ModeConfig = useAppSelector(selectConfig);
  const { userBrands, selectedTeam }: ModeContext = useAppSelector(selectContext);
  const sendEvent = useSendEvent({ mode });
  const sortProperty = selectedTeam?.teamType === 'RETAIL' ? 'retailSortOrder' : 'proSortOrder';

  const sortedBenefits = useMemo(() => BENEFITS.sort((a, b) => a[sortProperty] - b[sortProperty]), [sortProperty]);

  useEffect(() => {
    dispatch(fetchUserBrands(undefined));
  }, [dispatch]);

  return (
    <div
      id="banner-container"
      onClick={() => {
        sendEvent(`${ANALYTICS_PREFIX}CLICK_BANNER`);
      }}
    >
      <div className="perks-banner">
        <CarouselFlush itemsPerPage={itemsPerPage} smartArrows>
          {sortedBenefits.map((perk) => {
            const img = perk.image || getBrandCountImage(userBrands?.length || 0);
            if (img && !!perk[sortProperty]) {
              return (
                <div className="perk" key={perk.id}>
                  <img
                    src={img}
                    alt={getExpMessage({
                      defaultMessage: perk.titleDefault,
                      messageKey: `onb.post-aff-banner.title.${perk.id}`,
                    })}
                  />
                  <div className="perk-details">
                    <ExpLabel
                      className="perk-title"
                      defaultMessage={perk.titleDefault}
                      messageKey={`onb.post-aff.banner.title.${perk.id}`}
                    />
                    <ExpLabel
                      className="perk-text"
                      defaultMessage={perk.textDefault}
                      messageKey={`onb.post-aff.banner.text.${perk.id}`}
                    />
                    {!!perk.textAddendum && (
                      <ExpLabel
                        className="perk-addendum"
                        defaultMessage={perk.textAddendum}
                        messageKey={`onb.post-aff.banner.addendum.${perk.id}`}
                      />
                    )}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </CarouselFlush>
      </div>
    </div>
  );
});

PerksBanner.displayName = 'PerksBanner';

export default PerksBanner;
